import React, { useState } from 'react';
import {
  FileText,
  Users,
  Search,
  Filter,
  Download,
  Eye,
  RefreshCw,
  ArrowLeft,
  RotateCcw,
  Bell,
  CheckCircle
} from 'lucide-react';
import useLearningAssignmentToolStore from '@/stores/AdminStore/LearningAssignmentToolStore';
import { Assignment, UserProgress } from '@/services/PagesServices/LearningAssignmentToolService';
import './AssignmentHistory.css';

const AssignmentHistory: React.FC = () => {
  const { assignments, userProgress } = useLearningAssignmentToolStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [userFilter, setUserFilter] = useState<'all' | 'completed' | 'inprogress' | 'notstarted'>('all');

  const handleViewAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
  };

  const handleBackToHistory = () => {
    setSelectedAssignment(null);
  };

  const filteredUsers = userProgress?.filter(user => {
    if (userFilter === 'all') return true;
    if (userFilter === 'completed') return user.status === 'Completed';
    if (userFilter === 'inprogress') return user.status === 'In Progress';
    if (userFilter === 'notstarted') return user.status === 'Not Started';
    return true;
  }) || [];

  if (selectedAssignment) {
    return (
      <div className="trainer-ah--detail-container">
        <button className="trainer-ah--back-btn" onClick={handleBackToHistory}>
          <ArrowLeft size={16} />
          Back to Assignment History
        </button>

        <div className="trainer-ah--detail-header">
          <div>
            <h2 className="trainer-ah--detail-title">{selectedAssignment.name}</h2>
            <div className="trainer-ah--detail-meta">
              <span>Created: {selectedAssignment.assignedDate}</span>
              <span>•</span>
              <span>By: {selectedAssignment.assignedBy}</span>
              <span>•</span>
              <span>Due: {selectedAssignment.dueDate}</span>
            </div>
          </div>
          <button className="trainer-ah--btn trainer-ah--btn-primary">
            <RotateCcw size={16} />
            Resend Notifications
          </button>
        </div>

        <div className="trainer-ah--stats-grid">
          <div className="trainer-ah--stat-card">
            <div className="trainer-ah--stat-label">Total Users</div>
            <div className="trainer-ah--stat-value">{selectedAssignment.totalUsers}</div>
          </div>
          <div className="trainer-ah--stat-card trainer-ah--stat-completed">
            <div className="trainer-ah--stat-label">Completed</div>
            <div className="trainer-ah--stat-value">{selectedAssignment.doneCount}</div>
          </div>
          <div className="trainer-ah--stat-card trainer-ah--stat-progress">
            <div className="trainer-ah--stat-label">In Progress</div>
            <div className="trainer-ah--stat-value">{selectedAssignment.activeCount}</div>
          </div>
          <div className="trainer-ah--stat-card trainer-ah--stat-notstarted">
            <div className="trainer-ah--stat-label">Not Started</div>
            <div className="trainer-ah--stat-value">{selectedAssignment.pendingCount}</div>
          </div>
        </div>

        <div className="trainer-ah--user-filters">
          <button
            className={`trainer-ah--filter-btn ${userFilter === 'all' ? 'trainer-ah--filter-active' : ''}`}
            onClick={() => setUserFilter('all')}
          >
            All Users
          </button>
          <button
            className={`trainer-ah--filter-btn ${userFilter === 'completed' ? 'trainer-ah--filter-active' : ''}`}
            onClick={() => setUserFilter('completed')}
          >
            Completed
          </button>
          <button
            className={`trainer-ah--filter-btn ${userFilter === 'inprogress' ? 'trainer-ah--filter-active' : ''}`}
            onClick={() => setUserFilter('inprogress')}
          >
            In Progress
          </button>
          <button
            className={`trainer-ah--filter-btn ${userFilter === 'notstarted' ? 'trainer-ah--filter-active' : ''}`}
            onClick={() => setUserFilter('notstarted')}
          >
            Not Started
          </button>
        </div>

        <div className="trainer-ah--users-table-container">
          <table className="trainer-ah--users-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>STATUS</th>
                <th>PROGRESS</th>
                <th>LAST ACTIVITY</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="trainer-ah--user-cell">
                      <div className="trainer-ah--user-avatar">{user.avatar}</div>
                      <div>
                        <div className="trainer-ah--user-name">{user.name}</div>
                        <div className="trainer-ah--user-email">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="trainer-ah--status-badge trainer-ah--status-completed">
                      <CheckCircle size={14} />
                      {user.status}
                    </div>
                  </td>
                  <td>
                    <div className="trainer-ah--user-progress">
                      <span className="trainer-ah--progress-percentage">{user.progress}%</span>
                      <div className="trainer-ah--user-progress-bar">
                        <div
                          className="trainer-ah--user-progress-fill"
                          style={{ width: `${user.progress}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td>{user.lastActivity}</td>
                  <td>
                    <div className="trainer-ah--user-actions">
                      <button className="trainer-ah--action-btn" title="Notify">
                        <Bell size={16} />
                      </button>
                      <button className="trainer-ah--action-btn" title="View">
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="trainer-ah--history-container">
      <div className="trainer-ah--history-header">
        <div>
          <h2 className="trainer-ah--section-title">Assignment History</h2>
          <p className="trainer-ah--section-subtitle">View and manage all bulk assignments</p>
        </div>
        <div className="trainer-ah--history-actions">
          <button className="trainer-ah--btn trainer-ah--btn-icon">
            <Filter size={16} />
            Filter
          </button>
          <button className="trainer-ah--btn trainer-ah--btn-icon">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="trainer-ah--search-box">
        <Search size={18} className="trainer-ah--search-icon" />
        <input
          type="text"
          className="trainer-ah--search-input"
          placeholder="Search assignments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="trainer-ah--table-container">
        <table className="trainer-ah--table">
          <thead>
            <tr>
              <th>ASSIGNMENT NAME</th>
              <th>ASSIGNED DATE</th>
              <th>ASSIGNED BY</th>
              <th>TOTAL USERS</th>
              <th>COMPLETION STATUS</th>
              <th>DUE DATE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {assignments?.map((assignment) => (
              <tr key={assignment.id}>
                <td>
                  <div className="trainer-ah--assignment-cell">
                    <div className="trainer-ah--assignment-icon">
                      <FileText size={18} />
                    </div>
                    <div>
                      <div className="trainer-ah--assignment-name">{assignment.name}</div>
                      <div className="trainer-ah--assignment-items">{assignment.itemCount} learning items</div>
                    </div>
                  </div>
                </td>
                <td>{assignment.assignedDate}</td>
                <td>{assignment.assignedBy}</td>
                <td>
                  <div className="trainer-ah--users-cell">
                    <Users size={14} />
                    <span>{assignment.totalUsers}</span>
                  </div>
                </td>
                <td>
                  <div className="trainer-ah--completion-cell">
                    <div className="trainer-ah--completion-text">{assignment.completionPercentage}% complete</div>
                    <div className="trainer-ah--progress-bar">
                      <div
                        className="trainer-ah--progress-fill"
                        style={{ width: `${assignment.completionPercentage}%` }}
                      />
                    </div>
                    <div className="trainer-ah--status-tags">
                      <span className="trainer-ah--status-tag trainer-ah--status-done">{assignment.doneCount} done</span>
                      <span className="trainer-ah--status-tag trainer-ah--status-active">{assignment.activeCount} active</span>
                      <span className="trainer-ah--status-tag trainer-ah--status-pending">{assignment.pendingCount} pending</span>
                    </div>
                  </div>
                </td>
                <td>{assignment.dueDate}</td>
                <td>
                  <div className="trainer-ah--actions">
                    <button
                      className="trainer-ah--action-btn"
                      onClick={() => handleViewAssignment(assignment)}
                      title="View"
                    >
                      <Eye size={16} />
                    </button>
                    <button className="trainer-ah--action-btn" title="Refresh">
                      <RefreshCw size={16} />
                    </button>
                    <button className="trainer-ah--action-btn" title="Download">
                      <Download size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssignmentHistory;
